/* =====================================================================
   AstaLab 2.0 — AuctionRepository (obiettivo 8)

   Unico punto da cui la UI (e, in futuro, l'orchestrazione dell'asta
   live) legge/scrive lo stato persistito di un'asta. Non conosce
   Supabase: parla solo con un BackendAdapter (vedi backend/bootstrap.js)
   — lo stesso identico repository funziona sopra SupabaseBackendAdapter
   (deploy reale) o ArtifactDemoBackendAdapter (Artifact), perché
   entrambi rispettano lo stesso contratto BackendAdapter.

   "Il motore non deve conoscere direttamente Supabase" (obiettivo 8): il
   motore (frontend/src/engine/astalabEngine.js) non importa mai questo
   file né BackendAdapter — riceve/restituisce solo oggetti JS semplici.
   Questo repository è il confine di persistenza SOPRA al motore, mai
   dentro.

   Concorrenza ottimistica: saveAuction() non tenta MAI un retry
   automatico su conflitto — un merge automatico di due modifiche
   concorrenti allo stato di un'asta (roster, prezzi, storico acquisti)
   è una decisione che appartiene all'utente, non a questo livello. Il
   chiamante riceve {conflict:true} e decide come procedere (in questo
   round, tipicamente: ricaricare e riproporre la modifica).
   ===================================================================== */

export class AuctionRepository{
  /** @param {import("../backend/BackendAdapter.js").BackendAdapter} adapter */
  constructor(adapter){
    if(!adapter) throw new Error("AuctionRepository richiede un BackendAdapter.");
    this._adapter = adapter;
  }

  async listAuctions(){
    return this._adapter.listMyAuctions();
  }

  async createAuction({name, season}){
    return this._adapter.createAuction({name, season});
  }

  async renameAuction(auctionId, name){
    return this._adapter.renameAuction(auctionId, name);
  }

  async archiveAuction(auctionId){
    return this._adapter.archiveAuction(auctionId);
  }

  /**
   * @returns {Promise<{auctionId:string, revision:number, updatedAt:string|null, state:Object|null}>}
   * Non lancia mai e non restituisce mai null: un'asta senza stato salvato restituisce
   * revision:0/state:null, cosi' il chiamante può sempre fare `revision ?? 0` in sicurezza
   * al primo saveAuction() (vedi backend adapters: expectedRevision===0 significa "nessuna
   * riga precedente attesa").
   */
  async loadAuction(auctionId){
    const envelope = await this._adapter.loadAuctionState(auctionId);
    if(!envelope) return { auctionId, revision:0, updatedAt:null, state:null };
    return envelope;
  }

  /**
   * @param {string} auctionId
   * @param {Object} state - stato applicativo AstaLab (stessa forma della 1.3: players/config/weights/...)
   * @param {number} expectedRevision - revision letta dall'ultimo loadAuction() riuscito (0 se mai salvato)
   * @returns {Promise<{ok:boolean, conflict:boolean, revision:number|null, error:string|null}>}
   */
  async saveAuction(auctionId, state, expectedRevision){
    return this._adapter.saveAuctionState(auctionId, state, expectedRevision);
  }

  // ---------------- Lobby multiplayer / join-code (round alpha.4-r1) ----------------
  // Pass-through puro, come il resto di questo repository: nessuna business logic di
  // autorizzazione/idempotenza qui — vive interamente nell'adapter (demo) o nel database (RPC/RLS
  // reali), mai duplicata a questo livello.

  async joinAuctionByCode(rawCode){
    return this._adapter.joinAuctionByCode(rawCode);
  }
  async listAuctionMembers(auctionId){
    return this._adapter.listAuctionMembers(auctionId);
  }
  async rotateJoinCode(auctionId){
    return this._adapter.rotateJoinCode(auctionId);
  }
  async revokeJoinCode(auctionId){
    return this._adapter.revokeJoinCode(auctionId);
  }
  async getJoinCodeStatus(auctionId){
    return this._adapter.getJoinCodeStatus(auctionId);
  }
  async removeAuctionMember(auctionId, memberUserId){
    return this._adapter.removeAuctionMember(auctionId, memberUserId);
  }
  async leaveAuction(auctionId){
    return this._adapter.leaveAuction(auctionId);
  }

  // ---------------- Live auction: seat/squadra + lifecycle (round alpha.5-r1) ----------------
  // Pass-through puro, come il resto di questo repository: nessuna business logic qui — vive
  // interamente nell'adapter (demo) o nel database (RPC/RLS reali), mai duplicata a questo livello.

  async createAuctionSeat(auctionId, label){
    return this._adapter.createAuctionSeat(auctionId, label);
  }
  async deleteAuctionSeat(seatId){
    return this._adapter.deleteAuctionSeat(seatId);
  }
  async listAuctionSeats(auctionId){
    return this._adapter.listAuctionSeats(auctionId);
  }
  async assignMemberToSeat(seatId, userId){
    return this._adapter.assignMemberToSeat(seatId, userId);
  }
  async unassignMemberFromSeat(seatId){
    return this._adapter.unassignMemberFromSeat(seatId);
  }
  async loadLiveSession(auctionId){
    return this._adapter.loadLiveSession(auctionId);
  }
  async startLiveSession(auctionId, expectedRevision){
    return this._adapter.startLiveSession(auctionId, expectedRevision);
  }
  async pauseLiveSession(auctionId, expectedRevision){
    return this._adapter.pauseLiveSession(auctionId, expectedRevision);
  }
  async resumeLiveSession(auctionId, expectedRevision){
    return this._adapter.resumeLiveSession(auctionId, expectedRevision);
  }
  async closeLiveSession(auctionId, expectedRevision){
    return this._adapter.closeLiveSession(auctionId, expectedRevision);
  }

  // ---------------- Live auction: nomina/offerta/timer (round alpha.5-r4-r1) ----------------
  // Pass-through puro, come il resto di questo repository — nessuna business logic qui.

  async loadLiveRules(auctionId){
    return this._adapter.loadLiveRules(auctionId);
  }
  async configureLiveRules(auctionId, rules, expectedRevision){
    return this._adapter.configureLiveRules(auctionId, rules, expectedRevision);
  }
  async nominatePlayer(auctionId, seatId, fantacalcioId, expectedRevision, commandId){
    return this._adapter.nominatePlayer(auctionId, seatId, fantacalcioId, expectedRevision, commandId);
  }
  async placeBid(auctionId, nominationId, seatId, amount, expectedBidSeq, commandId){
    return this._adapter.placeBid(auctionId, nominationId, seatId, amount, expectedBidSeq, commandId);
  }
  async finalizeIfExpired(auctionId, nominationId){
    return this._adapter.finalizeIfExpired(auctionId, nominationId);
  }

  // ---------------- Live auction: settlement / purchase ledger / budget / roster (round alpha.5-r5-r1) ----------------
  // Pass-through puro, come il resto di questo repository — nessuna business logic qui.

  async loadRosterRules(auctionId){
    return this._adapter.loadRosterRules(auctionId);
  }
  async configureRosterRules(auctionId, initialBudgetPerSeat, expectedRevision, slotPOR = 3, slotDIF = 8, slotCEN = 8, slotATT = 6){
    return this._adapter.configureRosterRules(auctionId, initialBudgetPerSeat, expectedRevision, slotPOR, slotDIF, slotCEN, slotATT);
  }
  async listAuctionPurchases(auctionId){
    return this._adapter.listAuctionPurchases(auctionId);
  }
  async settleNomination(auctionId, nominationId){
    return this._adapter.settleNomination(auctionId, nominationId);
  }

  // ---------------- Live auction: current-nomination bid history (round A7-P1-5) ----------------
  // Pass-through puro, come tutto il resto di questo repository. Ambito auction-scoped (mai solo
  // nominationId da solo — A7-P1-5 amendment 4): auction_bids è vincolata sia da auction_id sia da
  // nomination_id lato schema/RLS, quindi il metodo riflette lo stesso ambito qui.
  async listBidsForNomination(auctionId, nominationId){
    return this._adapter.listBidsForNomination(auctionId, nominationId);
  }

  // ---------------- Live auction: auction-wide activity feed (round A7-P1-6) ----------------
  // Pass-through puro. auction_nominations è la sola fonte nuova da leggere (auction_purchases è
  // già letta da listAuctionPurchases() sopra, riusata invariata dal merge puro lato UI — mai una
  // seconda astrazione di lettura per lo stesso dato).
  async listAuctionNominations(auctionId){
    return this._adapter.listAuctionNominations(auctionId);
  }
}
